import React, { useEffect, useState, useRef, memo } from 'react';
import { Lock, Upload, MapPin, Save, CheckCircle, AlertCircle, Plus, Image as ImageIcon } from 'lucide-react';
interface Plan {
  id: string;
  name: string;
  imageUrl: string;
}
interface Pin {
  x: number;
  y: number;
}
export function PlanoTool() {
  // Auth State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState(false);
  // App State
  const [projectNameInput, setProjectNameInput] = useState('');
  const [projectName, setProjectName] = useState('');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planNameInput, setPlanNameInput] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [pin, setPin] = useState<Pin | null>(null);
  const [telefono, setTelefono] = useState('');
  const [sesionId, setSesionId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [saveErrorMsg, setSaveErrorMsg] = useState('');
  // Read URL Params on mount
  useEffect(() => {
    // Parse full URL to catch params even if they are before or after the hash
    const url = new URL(window.location.href);
    const tel = url.searchParams.get('telefono');
    const sesion = url.searchParams.get('sesion_id');
    if (tel) setTelefono(tel);
    if (sesion) setSesionId(sesion);
  }, []);
  // Cleanup object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      plans.forEach(plan => URL.revokeObjectURL(plan.imageUrl));
    };
  }, [plans]);
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'riezko') {
      setIsAuthenticated(true);
      setAuthError(false);
    } else {
      setAuthError(true);
    }
  };
  const handleCreateProject = () => {
    if (projectNameInput.trim()) {
      setProjectName(projectNameInput.trim());
    }
  };
  const handleAddPlan = () => {
    if (planNameInput.trim() && selectedFile) {
      const newPlan: Plan = {
        id: Math.random().toString(36).substring(2, 9),
        name: planNameInput.trim(),
        imageUrl: URL.createObjectURL(selectedFile)
      };
      setPlans([...plans, newPlan]);
      setPlanNameInput('');
      setSelectedFile(null);
      if (!selectedPlanId) {
        setSelectedPlanId(newPlan.id);
      }
    }
  };
  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setPin({
      x,
      y
    });
    setSaveStatus('idle');
    setSaveErrorMsg('');
  };
  const handleSavePin = async () => {
    setSaveStatus('idle');
    setSaveErrorMsg('');
    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    if (!telefono || !projectName || !selectedPlan || !pin) {
      setSaveStatus('error');
      setSaveErrorMsg('Faltan campos requeridos (Proyecto, Plano, Pin o Teléfono)');
      return;
    }
    if (!/^\+56\d{9}$/.test(telefono)) {
      setSaveStatus('error');
      setSaveErrorMsg('Teléfono inválido. Formato requerido: +56 seguido de 9 dígitos');
      return;
    }
    setIsSaving(true);
    const safeSessionId = sesionId || `web_${Date.now()}`;
    const payload = {
      telefono: `whatsapp:${telefono}`,
      sesion_id: safeSessionId,
      proyecto: projectName,
      plano: selectedPlan.name,
      x: Number(pin.x),
      y: Number(pin.y),
      estado: 'pendiente'
    };
    try {
      const response = await fetch('https://hook.us2.make.com/dgr6qsvlp5htc1tp31xge9qe0brbx3o9', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json().catch(() => null);
      console.log('Webhook response:', data);
      if (response.ok && data?.ok === true) {
        setSaveStatus('success');
        setPin(null);
      } else {
        setSaveStatus('error');
        setSaveErrorMsg('Error en servidor');
      }
    } catch (error) {
      console.error('Error guardando pin:', error);
      setSaveStatus('error');
      setSaveErrorMsg('Error de conexión');
    } finally {
      setIsSaving(false);
    }
  };
  if (!isAuthenticated) {
    return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-556">
        <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-557">
          <div className="flex justify-center" data-id="element-558">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-559">
              <Lock className="w-6 h-6 text-blue-600" data-id="element-560" />
            </div>
          </div>
          <h2 className="mt-6 text-center text-2xl font-bold text-gray-900" data-id="element-561">
            Acceso interno
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-562">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" data-id="element-563">
            <form className="space-y-6" onSubmit={handleLogin} data-id="element-564">
              <div data-id="element-565">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-566">
                  Clave
                </label>
                <div className="mt-1" data-id="element-567">
                  <input id="password" name="password" type="password" required value={password} onChange={e => setPassword(e.target.value)} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-568" />
                </div>
                {authError && <p className="mt-2 text-sm text-red-600 flex items-center" data-id="element-569">
                    <AlertCircle className="w-4 h-4 mr-1" data-id="element-570" /> Clave incorrecta
                  </p>}
              </div>

              <div data-id="element-571">
                <button type="submit" className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-572">
                  Entrar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>;
  }
  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  return <div className="min-h-screen bg-gray-50 pb-20" data-id="element-573">
      <header className="bg-white shadow-sm" data-id="element-574">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center" data-id="element-575">
          <MapPin className="w-6 h-6 text-blue-600 mr-2" data-id="element-576" />
          <h1 className="text-lg font-bold text-gray-900" data-id="element-577">
            Obra Digital - Planos
          </h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8" data-id="element-578">
        {/* SECTION 1 — PROYECTO */}
        <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-579">
          <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-580">
            1. Proyecto
          </h2>
          {!projectName ? <div className="flex flex-col sm:flex-row gap-3" data-id="element-581">
              <input type="text" placeholder="Nombre del proyecto" value={projectNameInput} onChange={e => setProjectNameInput(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-582" />
              <button onClick={handleCreateProject} disabled={!projectNameInput.trim()} className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-583">
                <Plus className="w-4 h-4 mr-2" data-id="element-584" /> Crear proyecto
              </button>
            </div> : <div className="flex items-center justify-between bg-blue-50 px-4 py-3 rounded-md border border-blue-100" data-id="element-585">
              <span className="text-sm font-medium text-blue-900" data-id="element-586">
                Proyecto actual:{' '}
                <span className="font-bold" data-id="element-587">{projectName}</span>
              </span>
              <button onClick={() => setProjectName('')} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-588">
                Cambiar
              </button>
            </div>}
        </section>

        {/* SECTION 2 — PLANOS */}
        <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-589">
          <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-590">
            2. Planos
          </h2>

          <div className="space-y-4" data-id="element-591">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-592">
              <div data-id="element-593">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-594">
                  Subir plano
                </label>
                <input type="file" accept="image/*" onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" data-id="element-595" />
              </div>
              <div data-id="element-596">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-597">
                  Nombre del plano
                </label>
                <div className="flex gap-2" data-id="element-598">
                  <input type="text" placeholder="Ej: Piso 1" value={planNameInput} onChange={e => setPlanNameInput(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-599" />
                  <button onClick={handleAddPlan} disabled={!planNameInput.trim() || !selectedFile} className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-600">
                    <Upload className="w-4 h-4" data-id="element-601" />
                  </button>
                </div>
              </div>
            </div>

            {plans.length > 0 && <div data-id="element-602">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-603">
                  Seleccionar plano
                </label>
                <select value={selectedPlanId} onChange={e => {
              setSelectedPlanId(e.target.value);
              setPin(null); // Reset pin when changing plan
            }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md border" data-id="element-604">
                  {plans.map(plan => <option key={plan.id} value={plan.id} data-id="element-605">
                      {plan.name}
                    </option>)}
                </select>
              </div>}
          </div>
        </section>

        {/* SECTION 3 — INTERACCIÓN CON PLANO */}
        <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-606">
          <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-607">
            3. Interacción con plano
          </h2>

          {!selectedPlan ? <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" data-id="element-608">
              <ImageIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-609" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-610">
                No hay plano seleccionado
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-611">
                Sube y selecciona un plano arriba.
              </p>
            </div> : <div className="space-y-4" data-id="element-612">
              <p className="text-sm text-gray-600 font-medium flex items-center" data-id="element-613">
                <MapPin className="w-4 h-4 mr-1 text-blue-600" data-id="element-614" /> Haz click en
                el plano para ubicar el pin
              </p>

              <div className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-100" data-id="element-615">
                <img src={selectedPlan.imageUrl} alt={selectedPlan.name} onClick={handleImageClick} className="w-full h-auto block cursor-crosshair" data-id="element-616" />
                {pin && <div className="absolute w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-md transform -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{
              left: `${pin.x * 100}%`,
              top: `${pin.y * 100}%`
            }} data-id="element-617" />}
              </div>

              {pin && <div className="flex gap-4 text-sm font-mono bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-618">
                  <div data-id="element-619">
                    <span className="text-gray-500" data-id="element-620">X:</span> {pin.x.toFixed(4)}
                  </div>
                  <div data-id="element-621">
                    <span className="text-gray-500" data-id="element-622">Y:</span> {pin.y.toFixed(4)}
                  </div>
                </div>}
            </div>}
        </section>

        {/* SECTION 4 — CONTEXTO USUARIO */}
        <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-623">
          <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-624">
            4. Contexto usuario
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-625">
            <div data-id="element-626">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-627">
                Teléfono
              </label>
              <input type="tel" value={telefono} onChange={e => {
              let val = e.target.value;
              // Only allow +, digits
              val = val.replace(/[^+\d]/g, '');
              // Force +56 prefix
              if (!val.startsWith('+56') && val.length > 0) {
                if (val.startsWith('+')) val = '+56' + val.slice(1).replace(/\+/g, '');else val = '+56' + val.replace(/\+/g, '');
              }
              // Limit to +56 + 9 digits = 12 chars
              if (val.length > 12) val = val.slice(0, 12);
              setTelefono(val);
            }} placeholder="+56912345678" maxLength={12} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-628" />
              {telefono && !/^\+56\d{9}$/.test(telefono) && <p className="mt-1 text-xs text-red-500" data-id="element-629">
                  Formato: +56 seguido de 9 dígitos
                </p>}
            </div>
            <div data-id="element-630">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-631">
                Sesión ID
              </label>
              <input type="text" value={sesionId} onChange={e => setSesionId(e.target.value)} placeholder="Ej: sess_123abc" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-632" />
            </div>
          </div>
        </section>

        {/* SECTION 5 — GUARDAR PIN */}
        <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-633">
          <h2 className="text-base font-semibold text-gray-900 mb-4" data-id="element-634">
            5. Guardar pin
          </h2>

          <div className="space-y-4" data-id="element-635">
            <button onClick={handleSavePin} disabled={isSaving || saveStatus === 'success' || !pin || !selectedPlan || !projectName} className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-636">
              {isSaving ? <span className="flex items-center" data-id="element-637">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-638">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-639"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-640"></path>
                  </svg>
                  Guardando...
                </span> : <span className="flex items-center" data-id="element-641">
                  <Save className="w-4 h-4 mr-2" data-id="element-642" /> Guardar ubicación
                </span>}
            </button>

            {saveStatus === 'success' && <div className="rounded-md bg-green-50 p-4 border border-green-200" data-id="element-643">
                <div className="flex" data-id="element-644">
                  <div className="flex-shrink-0" data-id="element-645">
                    <CheckCircle className="h-5 w-5 text-green-400" data-id="element-646" />
                  </div>
                  <div className="ml-3" data-id="element-647">
                    <p className="text-sm font-medium text-green-800" data-id="element-648">
                      Ubicación guardada correctamente
                    </p>
                  </div>
                </div>
              </div>}

            {saveStatus === 'error' && <div className="rounded-md bg-red-50 p-4 border border-red-200" data-id="element-649">
                <div className="flex" data-id="element-650">
                  <div className="flex-shrink-0" data-id="element-651">
                    <AlertCircle className="h-5 w-5 text-red-400" data-id="element-652" />
                  </div>
                  <div className="ml-3" data-id="element-653">
                    <p className="text-sm font-medium text-red-800" data-id="element-654">
                      {saveErrorMsg}
                    </p>
                  </div>
                </div>
              </div>}
          </div>
        </section>
      </main>
    </div>;
}